import { forwardRef, useState, useCallback, useMemo, useContext } from 'react';
import type { ReactNode, Ref, PropsWithoutRef, RefObject } from 'react';
import styled, { css } from 'styled-components';

import { useConsolidatedRef, useI18n, useFocusWithin, useEvent } from '../../hooks';
import { defaultThemeProp } from '../../theme';
import type { ForwardRefForwardPropsComponent, HeadingTag } from '../../types';
import Button from '../Button';
import { getFocusables } from '../../utils';
import Flex from '../Flex';
import Text from '../Text';
import { StyledCardContent } from '../Card/CardContent';
import { StyledCard } from '../Card/Card';
import { StyledCardHeader } from '../Card/CardHeader';

import type { TabsProps } from './Tabs.types';
import TabsContext from './TabsContext';
import { StyledAllTabsWrapper, StyledTabs } from './Tabs.styles';

interface TabPanelProps {
  /** The id of the Tab that the tab panel is related to. */
  tabId: string;
  /**
   * The current selected value of the tabs. Controls the visibility of the tab panel.
   * The tabsValue must match the tabId in order for the TabPanel to show itself.
   */
  currentTabId?: string;
  /** The title for the tab panel */
  title?: string;
  /** Content of the tab panel. */
  children: ReactNode;
  /** Ref to the tablist element. */
  tablistRef?: RefObject<HTMLElement>;
  /**
   * The tablist type indicates what direction the tabs are rendered in.
   * @default 'horizontal'
   */
  tablistType?: TabsProps['type'];
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLDivElement>;
}

const defaultProps: Partial<TabPanelProps> = {
  tabId: ''
};

export const StyledTabPanel = styled(Flex)<
  Pick<TabPanelProps, 'tablistType'> & {
    current: boolean;
    level: number;
  }
>(({ current, tablistType, level, theme }) => {
  return css`
    min-width: 0;
    max-width: 100%;

    &:focus-visible {
      outline: none;
      box-shadow: ${tablistType === 'horizontal' && level > 1
        ? theme.base.shadow['focus-inset']
        : theme.base.shadow.focus};
    }

    ${!current &&
    css`
      display: none;
    `}

    ${tablistType === 'horizontal' &&
    level > 1 &&
    css`
      background-color: ${theme.base.palette['primary-background']};
      border: 0.0625rem solid ${theme.base.palette['border-line']};
      border-block-start: none;
      border-end-start-radius: calc(0.5rem * 0.5);
      border-end-end-radius: calc(0.5rem * 0.5);
    `}

    ${theme.components.tabs.detached &&
    tablistType === 'horizontal' &&
    css`
      background: ${theme.base.palette['primary-background']};
      border: 0.0625rem solid ${theme.base.palette['border-line']};
      border-block-start: none;
      border-radius: var(--border-radius, ${theme.base['border-radius']});
      border-start-start-radius: 0;
      border-start-end-radius: 0;
      padding: calc(2 * ${theme.base.spacing}) calc(2 * ${theme.base.spacing});

      &&& ${StyledCardHeader}:not(${StyledCard} ${StyledCard} > ${StyledCardHeader}) {
        padding: 0;
        @media (width < ${theme.base.breakpoints.sm}) {
          padding: 0;
        }
      }

      &&& ${StyledCardContent} {
        padding: 0;

        ${StyledCard} {
          border: 0;
          padding: 0;
        }
      }

      &&& ${StyledCard} {
        padding: calc(2 * ${theme.base.spacing});
        padding-block-start: ${theme.base.spacing};
        border: 0.0625rem solid ${theme.base.palette['border-line']};
        border-radius: var(--border-radius, ${theme.base['border-radius']});
        background: ${theme.components.card.background};
      }

      &&& > ${StyledCard} {
        padding: 0;
        border: 0;
      }

      ${StyledAllTabsWrapper} {
        ${StyledTabs} {
          margin-block-end: 0;
        }
      }

      ${level > 1 &&
      css`
        :has(&) {
          ${StyledAllTabsWrapper} {
            ${StyledTabs} {
              margin: 0;
            }
          }
        }
      `}
    `}
  `;
});

StyledTabPanel.defaultProps = defaultThemeProp;

const StyledButton = styled(Button)(({ theme }) => {
  return css`
    padding-inline: calc(${theme.base.spacing});
    opacity: 0;

    &:focus {
      opacity: 1;
    }
  `;
});

StyledButton.defaultProps = defaultThemeProp;

const TabPanel: ForwardRefForwardPropsComponent<TabPanelProps> = forwardRef(function TabPanel(
  {
    tabId,
    currentTabId,
    title,
    tablistRef,
    tablistType = 'horizontal',
    children,
    ...restProps
  }: PropsWithoutRef<TabPanelProps>,
  ref: TabPanelProps['ref']
) {
  const t = useI18n();
  const tabPanelRef = useConsolidatedRef(ref);
  const [showReturnButton, setShowReturnButton] = useState(false);
  const { level } = useContext(TabsContext);

  const onFocusChange = useCallback((isFocused: boolean) => {
    if (!isFocused) {
      setShowReturnButton(false);
    }
  }, []);

  useFocusWithin([tabPanelRef], onFocusChange);

  const contextValue = useMemo(
    () => ({ level: tablistType === 'horizontal' ? level + 1 : 1 }),
    [level, tablistType]
  );

  const onKeyUp = () => {
    if (tabPanelRef.current?.contains(document.activeElement)) {
      setShowReturnButton(true);
    }
  };
  useEvent('keyup', onKeyUp);

  const currentTab = currentTabId && tabId ? currentTabId === tabId : true;

  return (
    <TabsContext.Provider value={contextValue}>
      <StyledTabPanel
        {...restProps}
        container={{
          direction: 'column',
          rowGap: 1,
          ...(tablistType === 'horizontal' && {
            pad: level > 1 ? [1, 1.5] : [1, undefined, undefined]
          })
        }}
        item={{ grow: 1 }}
        current={currentTab}
        inert={currentTab ? undefined : ''}
        role='tabpanel'
        aria-labelledby={`${tabId}`}
        id={`${tabId}-panel`}
        ref={tabPanelRef}
        tabIndex={0}
        tablistType={tablistType}
        level={level}
      >
        {title && <Text variant={`h${Math.min(2 + level, 6)}` as HeadingTag}>{title}</Text>}
        {children}
        {tablistRef?.current && showReturnButton && (
          <StyledButton
            variant='link'
            onClick={() => {
              getFocusables(tablistRef)[0]?.focus();
            }}
          >
            {t('return_to_tab_list')}
          </StyledButton>
        )}
      </StyledTabPanel>
    </TabsContext.Provider>
  );
});

TabPanel.defaultProps = defaultProps;

export default TabPanel;
